// router/routePreloader.js
class RoutePreloader {
  constructor() {
    this.loadedChunks = new Set();
    this.pendingLoads = new Map();

    // Route relationships mapping
    this.routeRelationships = {
      // Landing page: prefetch ONLY the primary next action (createtest).
      // Previously this also speculatively prefetched library/previoustests/
      // flashcards/performance — ~300-400 KB of routes most users never open in
      // a session, wasting bandwidth/CPU/mobile-data after first paint. Trimmed
      // to the single highest-probability destination.
      'IndexPage': [
        'createtest'
      ],

      'createtest': [
        'qbanktestinterfacePage',
        'searchPage',
        'fastmodePage'
      ],

      'qbanktestinterfacePage': [
        'testresults',
        'testanalysis',
        'notesPage',
        'searchPage'
      ],

      'main_library': [
        'subject_view',
        'searchPage',
        'studyPlanner'
      ],

      'subject_view': [
        'chapter_view',
        'lesson_view',
        'createtest'
      ],

      'chapter_view': [
        'lesson_view',
        'topic_group_view',
        'createtest'
      ],

      'lesson_view': [
        'notesPage',
        'createtest',
        'flashcards'
      ],

      'topic_group_view': [
        'lesson_view',
        'notesPage',
        'createtest'
      ],

      'previoustests': [
        'testresults',
        'testanalysis',
        'groupstudy',
        'qbanktestinterfacePage'
      ],

      'testresults': [
        'createtest',
        'performancePage_overall',
        'performancePage_reports',
        'notesPage',
        'groupstudy'
      ],

      'testanalysis': [
        'createtest',
        'performancePage_overall',
        'performancePage_reports',
        'notesPage',
        'groupstudy'
      ],

      'groupstudy': [
        'testresults',
        'testanalysis',
        'createtest'
      ],

      'flashcards': [
        'signleflashcardPage',
        'myflashcards',
        'createtest'
      ],

      'myflashcards': [
        'signleflashcardPage',
        'flashcards'
      ],

      'performancePage_overall': [
        'performancePage_reports',
        'createtest',
        'previoustests'
      ],

      'performancePage_reports': [
        'performancePage_overall',
        'createtest',
        'previoustests'
      ],

      'searchPage': [
        'qbanktestinterfacePage',
        'lesson_view',
        'notesPage'
      ],

      'notesPage': [
        'searchPage',
        'lesson_view',
        'createtest'
      ],

      'studyPlanner': [
        'StudyPlanCalendar',
        'createtest',
        'main_library'
      ],

      'StudyPlanCalendar': [
        'studyPlanner',
        'createtest',
        'main_library'
      ]
    };

    // Component import mapping
    this.componentImports = {
      'createtest': () => import(/* webpackChunkName: "page-createtest" */ 'pages/createtest.vue'),
      'qbanktestinterfacePage': () => import(/* webpackChunkName: "page-qbank-interface" */ 'pages/qbanktestinterfacePage.vue'),
      'main_library': () => import(/* webpackChunkName: "page-library-main" */ 'pages/library/main_library.vue'),
      'previoustests': () => import(/* webpackChunkName: "page-previoustests" */ 'pages/previoustestsPage.vue'),
      'subject_view': () => import(/* webpackChunkName: "page-library-subject" */ 'pages/library/subject_view.vue'),
      'chapter_view': () => import(/* webpackChunkName: "page-library-chapter" */ 'pages/library/chapter_view.vue'),
      'lesson_view': () => import(/* webpackChunkName: "page-library-lesson" */ 'pages/library/lesson_view.vue'),
      'topic_group_view': () => import(/* webpackChunkName: "page-library-topic-group" */ 'pages/library/topic_group_view.vue'),
      'testresults': () => import(/* webpackChunkName: "page-test-results" */ 'pages/results_testResults_prevTestsPage.vue'),
      'testanalysis': () => import(/* webpackChunkName: "page-test-analysis" */ 'pages/results_testAnalysis_prevTestsPage.vue'),
      'searchPage': () => import(/* webpackChunkName: "page-search" */ 'pages/searchPage.vue'),
      'notesPage': () => import(/* webpackChunkName: "page-notes" */ 'pages/notesPage.vue'),
      'flashcards': () => import(/* webpackChunkName: "page-flashcards" */ 'pages/flashcards.vue'),
      'signleflashcardPage': () => import(/* webpackChunkName: "page-flashcard-view" */ 'pages/signle_flashcard.vue'),
      'myflashcards': () => import(/* webpackChunkName: "page-my-flashcards" */ 'pages/myflashcardsPage.vue'),
      'performancePage_overall': () => import(/* webpackChunkName: "page-performance-overall" */ 'pages/performancePage_overall.vue'),
      'performancePage_reports': () => import(/* webpackChunkName: "page-performance-reports" */ 'pages/performancePage_reports.vue'),
      'studyPlanner': () => import(/* webpackChunkName: "page-study-planner" */ 'pages/StudyPlannerWizard.vue'),
      'StudyPlanCalendar': () => import(/* webpackChunkName: "page-study-calendar" */ 'pages/StudyPlanCalendar.vue'),
      'fastmodePage': () => import(/* webpackChunkName: "page-fastmode" */ 'pages/fastmode.vue'),
      'groupstudy': () => import(/* webpackChunkName: "page-group-study" */ 'pages/results_groupStudy_prevTestsPage.vue')
    };

    // Auto-initialize when router is ready
    this.initializeWhenReady();
  }

  initializeWhenReady() {
    // Wait for app to be ready
    if (document.readyState === 'complete') {
      this.preloadInitialRoutes();
    } else {
      window.addEventListener('load', () => {
        this.preloadInitialRoutes();
      });
    }
  }

  // Check network conditions.
  // Prefetching is a pure nicety — it must NEVER compete for bandwidth on
  // constrained connections. We treat 2g AND 3g as slow, honour Data Saver, and
  // also bail on low measured downlink / high round-trip time when the browser
  // exposes them. (ES5-safe: no optional chaining / nullish — Safari 12 floor.)
  isSlowConnection() {
    if ('connection' in navigator && navigator.connection) {
      const connection = navigator.connection;
      if (connection.saveData) {
        return true;
      }
      const slowTypes = ['slow-2g', '2g', '3g'];
      if (slowTypes.indexOf(connection.effectiveType) !== -1) {
        return true;
      }
      // Mbps; ~1.5 covers spotty 4G that behaves like 3g.
      if (typeof connection.downlink === 'number' && connection.downlink > 0 && connection.downlink < 1.5) {
        return true;
      }
      // Round-trip estimate in ms; >400ms means prefetch will hurt navigation.
      if (typeof connection.rtt === 'number' && connection.rtt > 400) {
        return true;
      }
    }
    return false;
  }

  // Load a single component
  async loadComponent(routeName) {
    if (this.loadedChunks.has(routeName) || this.pendingLoads.has(routeName)) {
      return;
    }

    const importFn = this.componentImports[routeName];
    if (!importFn) return;

    try {
      this.pendingLoads.set(routeName, true);
      await importFn();
      this.loadedChunks.add(routeName);
      console.log(`✓ Preloaded: ${routeName}`);
    } catch (error) {
      console.error(`Failed to preload ${routeName}:`, error);
    } finally {
      this.pendingLoads.delete(routeName);
    }
  }

  // Load multiple components when the browser is idle.
  //
  // Prefetch is ALWAYS skipped on slow connections / Data Saver, regardless of
  // priority — previously only 'low' was gated, so 'medium' route-relationship
  // prefetches still flooded constrained connections. Everything is scheduled
  // through requestIdleCallback so it can never compete with the app's own work
  // (initial render, the route the user actually navigated to, in-flight XHRs).
  async loadComponents(routeNames) {
    if (this.isSlowConnection()) {
      return;
    }

    const toLoad = routeNames.filter(name =>
      !this.loadedChunks.has(name) &&
      !this.pendingLoads.has(name) &&
      this.componentImports[name]
    );

    if (toLoad.length === 0) return;

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        toLoad.forEach(routeName => {
          requestIdleCallback(() => this.loadComponent(routeName), {
            timeout: 3000
          });
        });
      }, { timeout: 1000 });
    } else {
      // No requestIdleCallback (older Safari): defer well past first paint so
      // the prefetch never lands on the critical path.
      setTimeout(() => {
        toLoad.forEach(routeName => this.loadComponent(routeName));
      }, 1500);
    }
  }

  // Initial preload of core components.
  //
  // NOTE: 'qbanktestinterfacePage' was DELIBERATELY removed from this list.
  // It is the heaviest route in the app and (via notesForm.vue) used to drag the
  // entire CKEditor build (~468 KB / 1.8 MB decoded) onto the landing page cold
  // load, even though a student on the home screen has not created a test yet.
  // The test interface is reached only via createtest -> so it is now prefetched
  // *from* createtest (see routeRelationships), not on first paint.
  //
  // This whole prefetch pass respects slow connections / Data Saver via
  // loadComponents()'s isSlowConnection() gate, and runs only after first paint.
  //
  // Trimmed to JUST 'createtest' — the single highest-probability next action.
  // Previously prefetched createtest+library+previoustests on every app load,
  // speculatively pulling routes many users never open. createtest's own
  // relationships still fan out (search/fastmode/qbank-interface) once the user
  // is actually on createtest.
  preloadInitialRoutes() {
    console.log('Starting initial route preload...');

    const coreRoutes = [
      'createtest'
    ];

    setTimeout(() => {
      this.loadComponents(coreRoutes);
    }, 500);
  }

  // Preload based on current route
  preloadForRoute(routeName) {
    const relatedRoutes = this.routeRelationships[routeName];

    if (relatedRoutes && relatedRoutes.length > 0) {
      console.log(`Preloading routes related to: ${routeName}`);
      this.loadComponents(relatedRoutes);
    }
  }

  // Cancel all pending loads
  cancelPendingLoads() {
    this.pendingLoads.clear();
  }

  // Get preload statistics
  getStats() {
    return {
      loaded: this.loadedChunks.size,
      pending: this.pendingLoads.size,
      total: Object.keys(this.componentImports).length,
      loadedRoutes: Array.from(this.loadedChunks)
    };
  }
}

// Create singleton instance
export const routePreloader = new RoutePreloader();

// Make available in development
if (process.env.DEV) {
  window.routePreloader = routePreloader;
}
